import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/services/api';

/**
 * Server state lives here, not in component `useState`.
 *
 * v1's largest page held 47 `useState` calls, most of them the same four
 * variables repeated per resource: data, loading, error, and a refresh counter.
 * There was also a `PageRefreshContext` whose only job was to bump a `key` and
 * remount the whole page after a mutation. Query replaces all of it.
 *
 * Rules for this app specifically:
 *  - Reference data (classes, sections, fee heads) barely changes within a
 *    shift, so it is cached for the session rather than refetched per screen.
 *  - Money must never be stale: anything under an `invoices` or `transactions`
 *    key sets its own short staleTime and refetches on window focus.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Office staff work one screen for long stretches; refetching on every
      // focus change is noise. Screens showing money opt back in explicitly.
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      retry: (failureCount, error) => {
        // A 4xx will fail identically on retry — only retry infrastructure
        // failures, and only twice before showing the error state.
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Query keys, centralised.
 *
 * Invalidation is only reliable if every call site spells a key the same way.
 * Building them here means `qk.students.list(params)` and the invalidation that
 * follows a mutation cannot drift apart.
 */
export const qk = {
  students: {
    all: ['students'] as const,
    list: (params: Record<string, unknown>) => ['students', 'list', params] as const,
    detail: (id: string) => ['students', 'detail', id] as const,
  },
  classes: {
    all: ['classes'] as const,
    list: (params: Record<string, unknown>) => ['classes', 'list', params] as const,
    sections: (classId: string) => ['classes', classId, 'sections'] as const,
    /** Subjects allocated to a class, optionally narrowed to one section. */
    subjects: (classId: string, sectionId: string) =>
      ['classes', classId, 'subjects', sectionId] as const,
  },
  staff: {
    all: ['staff'] as const,
    list: (params: Record<string, unknown>) => ['staff', 'list', params] as const,
    detail: (id: string) => ['staff', 'detail', id] as const,
  },
  exams: {
    all: ['exams'] as const,
    list: (params: Record<string, unknown>) => ['exams', 'list', params] as const,
    schedules: (examId: string) => ['exams', examId, 'schedules'] as const,
    results: (examId: string, scheduleId: string) =>
      ['exams', examId, 'schedules', scheduleId, 'results'] as const,
  },
  /* Attendance. The sheet is keyed on the whole context — date, class, section
     — because that tuple *is* the document being edited: two sections on the
     same day are different sheets, and a cached one must never be shown under
     the other's heading. The monthly register is keyed on the month instead,
     since it is one request for the whole matrix. */
  attendance: {
    all: ['attendance'] as const,
    students: (params: Record<string, unknown>) => ['attendance', 'students', params] as const,
    staff: (params: Record<string, unknown>) => ['attendance', 'staff', params] as const,
    monthly: (params: Record<string, unknown>) => ['attendance', 'monthly', params] as const,
  },
  /* Homework and the submissions graded against it.

     `submissions` and `statuses` are the same rows read at two weights: the
     evaluation grid needs the marks, the ledger's completion bar needs only a
     count. They are separate keys so opening one homework to grade it does not
     evict the light counts the register behind it is showing. */
  homework: {
    all: ['homework'] as const,
    list: (params: Record<string, unknown>) => ['homework', 'list', params] as const,
    detail: (id: string) => ['homework', 'detail', id] as const,
    submissions: (homeworkId: string) => ['homework', homeworkId, 'submissions'] as const,
    statuses: (homeworkId: string) => ['homework', homeworkId, 'statuses'] as const,
  },
  /* The timetable of one class, or one section of it. Keyed on the whole
     context — session, class, section — for the reason the attendance sheet is:
     that tuple *is* the document being edited, and a cached grid must never be
     shown under another section's heading. */
  timetable: {
    all: ['timetable'] as const,
    grid: (params: Record<string, unknown>) => ['timetable', 'grid', params] as const,
  },
  /* Staff tasks. `detail` is read by `?edit=` when the row is not on the page
     the current filter shows — the list and the member route return the same
     record, so the form is happy with either. */
  tasks: {
    all: ['tasks'] as const,
    list: (params: Record<string, unknown>) => ['tasks', 'list', params] as const,
    detail: (id: string) => ['tasks', 'detail', id] as const,
  },
  /* Notices. `detail` is read by `?edit=` when the row is not on the page the
     register is showing — the list and the member route return the same record,
     so the form is happy with either. Same shape as `tasks` for that reason. */
  notices: {
    all: ['notices'] as const,
    list: (params: Record<string, unknown>) => ['notices', 'list', params] as const,
    detail: (id: string) => ['notices', 'detail', id] as const,
  },
  /* Holidays. `session` is the whole of one school-year's holidays rather than a
     page of them — the calendar view can be paged to any month, so a page could
     not fill it, and the chronological order the directives require is only
     meaningful over a complete set. See `fetchSessionHolidays`. The school and
     session are *in* the key because switching either in the title bar must show
     a different year's calendar rather than the previous one's. */
  holidays: {
    all: ['holidays'] as const,
    session: (schoolId: string, sessionId: string) =>
      ['holidays', 'session', schoolId, sessionId] as const,
    detail: (id: string) => ['holidays', 'detail', id] as const,
  },
  /* Gate passes. The two read methods of the resource and no more — nothing
     here creates, updates or deletes one; see the note at the top of
     `gatepassApi.ts` for why. `detail` is what `?pass=` falls back to when the
     row is not on the page the current filter shows, which on this register is
     the common case rather than the edge one: a link to a pass is followed the
     next day, by which point the register has moved on to a different date. */
  gatePasses: {
    all: ['gate-passes'] as const,
    list: (params: Record<string, unknown>) => ['gate-passes', 'list', params] as const,
    detail: (id: string) => ['gate-passes', 'detail', id] as const,
  },
  schools: {
    all: ['schools'] as const,
    list: (params: Record<string, unknown>) => ['schools', 'list', params] as const,
    detail: (id: string) => ['schools', 'detail', id] as const,
  },
  // No `list(params)`: the endpoint takes no parameters and returns every row.
  organizations: {
    all: ['organizations'] as const,
  },
  subjects: {
    all: ['subjects'] as const,
    /** The board's catalogue — paged by the server. */
    board: (boardId: number, params: Record<string, unknown>) =>
      ['subjects', 'board', boardId, params] as const,
    /** What this organization has adopted — fetched whole, see subjectApi. */
    adopted: ['subjects', 'adopted'] as const,
    /** Which of those a given school has enabled. */
    allocations: (schoolId: string) => ['subjects', 'allocations', schoolId] as const,
  },
  reference: {
    boards: ['reference', 'boards'] as const,
    /** Shared by the Sessions register and the title bar's session switcher —
        creating or renaming one there updates the chip without a reload. */
    academicSessions: ['reference', 'academic-sessions'] as const,
    /** The semesters of one session. Keyed by session because a term outside
        its own session is meaningless — there is no useful "all terms" list. */
    academicTerms: (sessionId: string) =>
      ['reference', 'academic-sessions', sessionId, 'terms'] as const,
    schoolSubjects: (schoolId: string) => ['reference', 'school-subjects', schoolId] as const,
    departments: ['reference', 'departments'] as const,
    designations: ['reference', 'designations'] as const,
    roles: ['reference', 'roles'] as const,
    streams: ['reference', 'streams'] as const,
    states: ['reference', 'states'] as const,
    districts: (stateId: number) => ['reference', 'states', stateId, 'districts'] as const,
    cities: (stateId: number) => ['reference', 'states', stateId, 'cities'] as const,
  },
  /* Transport. `routeStops` is keyed per route and is read twice over: the
     Route Builder's sequence pane shows one route's stops, and its list shows
     every route's stop count — the routes response carries no count of its own.
     One key for both means selecting a route costs no request. */
  transport: {
    routes: ['transport', 'routes'] as const,
    /* Deliberately not nested under `routes`: keyed as a child, invalidating
       the route list after a rename would refetch every route's stops with it.
       `allRouteStops` is the prefix, for the one case that does have to reach
       every route — deleting a stop from the catalogue. */
    allRouteStops: ['transport', 'route-stops'] as const,
    routeStops: (routeId: string) => ['transport', 'route-stops', routeId] as const,
    /** The organization-wide stop catalogue, shared by every route. */
    stops: ['transport', 'stops'] as const,
    vehicles: ['transport', 'vehicles'] as const,
    drivers: ['transport', 'drivers'] as const,
    /** One page of allocations — the student register's Route column join. */
    allocations: ['transport', 'allocations'] as const,
    /* The whole allocation table, page-walked (`fetchAllocationRegister`).
       Deliberately *under* `allocations` rather than beside it: the two hold
       the same rows at different completeness, and an assignment made on the
       passenger screen has to reach the student register's Route column too —
       so one `invalidateQueries({ queryKey: qk.transport.allocations })`
       catches both by prefix. */
    allocationRegister: ['transport', 'allocations', 'register'] as const,
  },
  invoices: {
    all: ['invoices'] as const,
    list: (params: Record<string, unknown>) => ['invoices', 'list', params] as const,
    forStudent: (studentId: string) => ['invoices', 'student', studentId] as const,
    detail: (id: string) => ['invoices', 'detail', id] as const,
  },
  /* What a student owes, computed on demand — outstanding invoices *and* the
     months that have not been billed yet (`/api/students/{id}/fee-dues`).
     Deliberately not under `invoices`: half of what it returns is not an
     invoice and never will be until a payment raises one. It is invalidated
     alongside invoices after every payment, because taking money changes both. */
  dues: {
    all: ['dues'] as const,
    forStudent: (studentId: string, sessionId: string) =>
      ['dues', 'student', studentId, sessionId] as const,
  },
  transactions: {
    all: ['transactions'] as const,
    list: (params: Record<string, unknown>) => ['transactions', 'list', params] as const,
    /** One record, fetched by id — for a `?tx=` restored from a pasted link
        whose row is not on the page the filter currently shows. */
    record: (id: string) => ['transactions', 'record', id] as const,
    /** What a receipt needs for one payment — see `resolvePaymentDetail`.
        Shared by the ledger's row print and the detail drawer, so opening a
        record and then printing it costs one request, not two. */
    detail: (id: string) => ['transactions', 'detail', id] as const,
  },
  /* Money going out. `totals` is keyed on the same filter as `list` and walks
     every page of it — see `fetchExpenseTotals`. Categories are unpaged and
     shared by both expense screens, so adding one on the categories register
     puts it in the ledger's filter and the entry form immediately. */
  expenses: {
    all: ['expenses'] as const,
    list: (params: Record<string, unknown>) => ['expenses', 'list', params] as const,
    totals: (params: Record<string, unknown>) => ['expenses', 'totals', params] as const,
    categories: ['expenses', 'categories'] as const,
  },
  /* Fee *setup* — what is charged, as opposed to what has been billed. Heads
     are organization-wide; structures are scoped to a school and a session, and
     that scope is in the key because switching either in the title bar must
     show different amounts rather than the previous school's. */
  fees: {
    all: ['fees'] as const,
    heads: ['fees', 'heads'] as const,
    structures: (schoolId: string, sessionId: string) =>
      ['fees', 'structures', schoolId, sessionId] as const,
    discounts: (schoolId: string) => ['fees', 'discounts', schoolId] as const,
    /** The discount one student is on. Read per student because there is no
        bulk route — `/api/student-fee-discounts` 404s. */
    studentDiscount: (studentId: string) => ['fees', 'student-discount', studentId] as const,
  },
  /* Server-computed reports (`/api/reports`). Deliberately their own namespace
     rather than nested under `fees` or `invoices`: a report is a *reading* of
     records those keys own, so a fee edit should invalidate the report, but
     invalidating the report must not evict the records. The class fee summary
     is keyed on the class, section and session it was computed for — change any
     of the three and it is a different report, not a stale one. */
  reports: {
    all: ['reports'] as const,
    classFeeSummary: (params: Record<string, unknown>) =>
      ['reports', 'class-fee-summary', params] as const,
    /** Every route one driver covers, with its fares. Keyed on the session
        because the fares are a session's, and switching year in the title bar
        must re-ask rather than show last year's run. */
    driverManifest: (driverId: string, sessionId: string | null) =>
      ['reports', 'driver-manifest', driverId, sessionId] as const,
  },
} as const;

/** Turns any thrown value into copy a user can act on. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong. Try again.';
}

/**
 * Laravel's per-field validation messages, flattened to one per field.
 *
 * A 422 carries `{ errors: { email: ["The email field is required."] } }`. Left
 * unread, the user gets a generic failure and has to open the network tab to
 * find out which field the server objected to — so every form that submits
 * should merge this into its own field-error state.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError) || !error.errors) return {};
  const out: Record<string, string> = {};
  for (const [field, messages] of Object.entries(error.errors)) {
    if (messages?.[0]) out[field] = messages[0];
  }
  return out;
}

/**
 * The same messages as a flat list, for a form-level summary.
 *
 * Mapping to fields is not enough on its own: the server can reject on a field
 * the form does not render, and a user who has scrolled to the save button will
 * not see an inline error that landed near the top. Show both.
 */
export function errorList(error: unknown): string[] {
  const fields = Object.values(fieldErrors(error));
  return fields.length > 0 ? fields : [errorMessage(error)];
}
